import { Button, Divider, Input, Progress, Spinner } from '@nextui-org/react'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import {
  CreditBalanceResponse,
  CreditPacksResponse,
  getCreditBalance,
  getCreditPacks,
  purchaseCredits
} from '~/billing/services/billingService'
import { Typography } from '~/components/common/Typography'
import { useBillingContext } from '~/contexts/BillingContext'

interface CreditPurchaseCardProps {
  onPurchaseComplete?: () => void
  /** 'full' = balance + purchase (default), 'balance' = balance info only, 'purchase' = buy credits only */
  variant?: 'full' | 'balance' | 'purchase'
  /** Hide purchase UI (e.g. super admin view-only manage account). */
  readOnly?: boolean
}

export function CreditPurchaseCard({
  onPurchaseComplete: _onPurchaseComplete,
  variant = 'full',
  readOnly = false
}: CreditPurchaseCardProps) {
  const { billingInfo, allLicenses } = useBillingContext()

  // Detect if user is on Flex plan (credits never expire)
  const isFlexPlan = allLicenses?.licenses?.some(
    (lic: { license_name?: string; instance_metadata?: { is_flex?: boolean } }) =>
      lic.license_name === 'flex' || lic.instance_metadata?.is_flex
  )
  const [packsData, setPacksData] = useState<CreditPacksResponse | null>(null)
  const [balance, setBalance] = useState<CreditBalanceResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [customAmount, setCustomAmount] = useState<string>('')

  const billingId = billingInfo?.id

  useEffect(() => {
    if (billingId) {
      loadData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData is stable per variant/billingId
  }, [billingId, variant])

  const loadData = async () => {
    if (!billingId) return

    try {
      setLoading(true)
      setError(null)
      if (variant === 'balance') {
        // Balance-only mode: only fetch balance data
        const balanceData = await getCreditBalance(billingId)
        setBalance(balanceData)
      } else if (variant === 'purchase') {
        // Purchase-only mode: only fetch packs data
        const packs = await getCreditPacks()
        setPacksData(packs)
      } else {
        // Full mode: fetch both
        const [packs, balanceData] = await Promise.all([
          getCreditPacks(),
          getCreditBalance(billingId)
        ])
        setPacksData(packs)
        setBalance(balanceData)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit data')
    } finally {
      setLoading(false)
    }
  }

  const handlePurchase = async (packType: string | null, amount?: number) => {
    if (!billingId) {
      setError('Billing account not found')
      return
    }

    try {
      const purchaseId = packType || `custom-${amount}`
      setPurchasing(purchaseId)
      setError(null)

      const currentUrl = window.location.href.split('?')[0]
      const result = await purchaseCredits(
        billingId,
        packType,
        `${currentUrl}?purchase=success`,
        `${currentUrl}?purchase=cancelled`,
        amount
      )

      window.location.href = result.redirect_url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate purchase')
      setPurchasing(null)
    }
  }

  const minCredits = packsData?.min_custom_amount || 25
  const maxCredits = packsData?.max_custom_amount || 10_000

  const handleCustomPurchase = () => {
    const amount = parseInt(customAmount, 10)
    if (isNaN(amount) || amount < minCredits || amount > maxCredits) {
      setError(`Enter a valid amount between ${minCredits} and ${maxCredits}`)
      return
    }
    handlePurchase(null, amount)
  }

  const pricePerCredit = (packsData?.price_per_credit_cents || 65) / 100
  const customAmountNum = parseInt(customAmount, 10) || 0
  const customPrice = customAmountNum * pricePerCredit

  if (!billingId || loading) {
    return (
      <div
        className={`flex items-center justify-center self-stretch ${variant === 'purchase' ? 'py-2' : 'py-12'}`}
      >
        <Spinner size={variant === 'purchase' ? 'sm' : 'lg'} />
      </div>
    )
  }

  const subscriptionUsagePercent = balance
    ? Math.min(100, (balance.subscription_used / Math.max(1, balance.subscription_limit)) * 100)
    : 0

  const showBalance = variant === 'full' || variant === 'balance'
  const showPurchase = (variant === 'full' || variant === 'purchase') && !readOnly

  return (
    <div className='flex flex-col items-start gap-6 self-stretch'>
      {/* Section Header */}
      {showBalance && (
        <div className='flex flex-col items-start gap-1 self-stretch'>
          <Typography
            fontFamily='geist'
            fontSize='titleSm'
            fontWeight='semibold'
            className='text-[rgba(0,0,0,0.88)]'
          >
            Credit Balance
          </Typography>
          <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500'>
            {isFlexPlan
              ? 'Your purchased credits (never expire)'
              : 'Available credits for this billing period'}
          </Typography>
        </div>
      )}

      {error && (
        <div className='p-3 rounded-lg bg-red-50 border border-red-200 self-stretch'>
          <Typography fontFamily='geist' fontSize='bodySm' className='text-red-700'>
            {error}
          </Typography>
        </div>
      )}

      {/* Two Column Layout (full) or single column (balance/purchase only) */}
      <div
        className={`flex gap-8 self-stretch ${variant === 'full' ? 'flex-col lg:flex-row' : 'flex-col'}`}
      >
        {/* Balance Info */}
        {showBalance && (
          <div className='flex flex-col gap-5 flex-1'>
            {balance && (
              <>
                {/* Total Balance */}
                <div className='flex items-center justify-between p-4 rounded-xl bg-[#F4F1FE] border border-[#E8E3FC]'>
                  <div className='flex flex-col gap-1'>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelMd'
                      fontWeight='semibold'
                      className='text-[#6B5CB3]'
                    >
                      Total Available
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='titleLg'
                      fontWeight='bold'
                      className='text-[#4A3F8C]'
                    >
                      {balance.total_credits.toLocaleString()}
                    </Typography>
                  </div>
                  {(balance.subscription_limit > 0 || balance.purchased_credits > 0) && (
                    <div className='flex flex-col items-end gap-1'>
                      {balance.subscription_limit > 0 && (
                        <Typography fontFamily='geist' fontSize='bodySm' className='text-[#6B5CB3]'>
                          Subscription: {balance.subscription_remaining.toLocaleString()}
                        </Typography>
                      )}
                      {balance.purchased_credits > 0 && (
                        <Typography fontFamily='geist' fontSize='bodySm' className='text-[#6B5CB3]'>
                          Purchased: {balance.purchased_credits.toLocaleString()}
                        </Typography>
                      )}
                    </div>
                  )}
                </div>

                {/* Subscription Usage — only shown when subscription has credits */}
                {balance.subscription_limit > 0 && (
                  <div className='flex flex-col gap-2'>
                    <div className='flex justify-between items-center'>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelMd'
                        fontWeight='semibold'
                        className='text-[rgba(0,0,0,0.65)]'
                      >
                        Subscription Usage
                      </Typography>
                      <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500'>
                        {balance.subscription_used.toLocaleString()} /{' '}
                        {balance.subscription_limit.toLocaleString()} used
                      </Typography>
                    </div>
                    <Progress
                      value={subscriptionUsagePercent}
                      color={subscriptionUsagePercent > 80 ? 'warning' : 'primary'}
                      size='sm'
                      className='w-full'
                    />
                  </div>
                )}

                {/* Purchased Credits — only shown when purchased credits exist */}
                {balance.purchased_credits > 0 && (
                  <div className='flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200'>
                    <div className='flex flex-col gap-0.5'>
                      <Typography
                        fontFamily='geistMono'
                        fontSize='labelMd'
                        fontWeight='semibold'
                        className='text-[rgba(0,0,0,0.65)]'
                      >
                        Purchased Credits
                      </Typography>
                      <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500'>
                        Additional credits you&apos;ve bought
                      </Typography>
                    </div>
                    <Typography
                      fontFamily='geist'
                      fontSize='titleMd'
                      fontWeight='bold'
                      className='text-[rgba(0,0,0,0.88)]'
                    >
                      {balance.purchased_credits.toLocaleString()}
                    </Typography>
                  </div>
                )}

                {/* Billing Period */}
                {balance.period_end && (
                  <div className='flex items-center gap-2'>
                    <svg
                      className='w-4 h-4 text-gray-400'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500'>
                      Credits expire:{' '}
                      <span className='font-semibold text-[rgba(0,0,0,0.88)]'>
                        {format(new Date(balance.period_end), 'MMMM dd, yyyy')}
                      </span>
                    </Typography>
                  </div>
                )}

                {/* Recent Purchases */}
                {balance.purchase_history.length > 0 && (
                  <div className='flex flex-col gap-2'>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelMd'
                      fontWeight='semibold'
                      className='text-[rgba(0,0,0,0.65)]'
                    >
                      Recent Purchases
                    </Typography>
                    <div className='flex flex-col gap-1'>
                      {balance.purchase_history
                        .filter(p => !p.refunded)
                        .slice(0, 3)
                        .map((purchase, idx) => (
                          <div key={idx} className='flex justify-between py-1'>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              className='text-green-600 font-medium'
                            >
                              +{purchase.credits} credits
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodySm'
                              className='text-gray-500'
                            >
                              {purchase.purchased_at
                                ? format(new Date(purchase.purchased_at), 'MMM dd, yyyy')
                                : 'Recently'}
                            </Typography>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Vertical Divider - only in full mode */}
        {variant === 'full' && (
          <Divider orientation='vertical' className='hidden lg:block h-auto' />
        )}

        {/* Purchase Options */}
        {showPurchase && variant !== 'purchase' && (
          <div className='flex flex-col gap-5 flex-1'>
            <div className='flex flex-col gap-1'>
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='semibold'
                className='text-[rgba(0,0,0,0.88)]'
              >
                Buy More Credits
              </Typography>
              <Typography fontFamily='geist' fontSize='bodySm' className='text-gray-500'>
                {isFlexPlan ? 'Credits never expire' : 'Expires at billing period end'}
              </Typography>
            </div>

            {/* Credit Packs */}
            <div className='grid grid-cols-2 gap-3'>
              {packsData?.packs.map(pack => (
                <Button
                  key={pack.id}
                  variant='bordered'
                  className='h-auto py-4 flex flex-col items-center gap-0.5 hover:bg-[#F4F1FE] hover:border-[#846FE1] transition-colors border-gray-200'
                  isDisabled={purchasing !== null}
                  isLoading={purchasing === pack.id}
                  onPress={() => handlePurchase(pack.id)}
                >
                  <span className='text-xl font-bold text-[rgba(0,0,0,0.88)]'>{pack.credits}</span>
                  <span className='text-xs text-gray-500'>Credits for</span>
                  <span className='text-sm font-semibold text-[#846FE1]'>{pack.price_display}</span>
                </Button>
              ))}
            </div>

            <Divider />

            {/* Custom Amount */}
            <div className='flex flex-col gap-3'>
              <Typography
                fontFamily='geistMono'
                fontSize='labelMd'
                fontWeight='semibold'
                className='text-[rgba(0,0,0,0.65)]'
              >
                Custom Amount
              </Typography>
              <div className='flex gap-3'>
                <Input
                  type='number'
                  label='Number of Credits'
                  placeholder={`${minCredits} - ${maxCredits}`}
                  min={minCredits}
                  max={maxCredits}
                  value={customAmount}
                  onValueChange={setCustomAmount}
                  variant='bordered'
                  size='sm'
                  className='flex-1'
                  description={
                    customAmountNum >= minCredits
                      ? `Total: $${customPrice.toFixed(2)}`
                      : `Minimum ${minCredits} credits`
                  }
                />
                <Button
                  className='h-12 px-6 self-start bg-[#846FE1] text-white hover:bg-[#6B5CB3]'
                  isDisabled={purchasing !== null || customAmountNum < minCredits}
                  isLoading={purchasing?.startsWith('custom-')}
                  onPress={handleCustomPurchase}
                >
                  Buy
                </Button>
              </div>
            </div>

            {/* Info Note */}
            <div className='p-3 rounded-lg bg-blue-50 border border-blue-200'>
              <Typography fontFamily='geist' fontSize='bodySm' className='text-blue-700'>
                <span className='font-semibold'>Note:</span> Purchased credits are added immediately
                {isFlexPlan
                  ? ' and never expire. Credits will roll over if you upgrade to a subscription plan.'
                  : ' and expire at the end of your current billing period.'}
              </Typography>
            </div>
          </div>
        )}

        {/* Compact Purchase Layout - for purchase-only variant (used in PlanDialog) */}
        {variant === 'purchase' && (
          <div className='flex flex-col gap-3 self-stretch'>
            {/* Pack buttons row */}
            <div className='flex items-center gap-2 self-stretch'>
              {packsData?.packs.map(pack => (
                <button
                  key={pack.id}
                  disabled={purchasing !== null}
                  onClick={() => handlePurchase(pack.id)}
                  className='flex-1 h-11 inline-flex items-center justify-center gap-2 rounded-xl border border-tokens-neutral-border-1 bg-white transition-all hover:border-tokens-primary-2 hover:bg-[#F4F1FE] hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'
                >
                  {purchasing === pack.id ? (
                    <Spinner size='sm' className='w-4 h-4' />
                  ) : (
                    <>
                      <span className='text-sm font-bold text-tokens-neutral-text-primary'>
                        {pack.credits} Credits
                      </span>
                      <span className='text-sm font-medium text-tokens-neutral-text-secondary'>
                        for
                      </span>
                      <span className='text-sm font-semibold text-tokens-primary-2'>
                        {pack.price_display}
                      </span>
                    </>
                  )}
                </button>
              ))}
            </div>

            {/* Custom amount row */}
            <div className='flex items-center gap-2 self-stretch'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='medium'
                className='text-tokens-neutral-text-secondary whitespace-nowrap'
              >
                Custom
              </Typography>
              <Input
                type='number'
                placeholder={`${minCredits} – ${maxCredits} credits`}
                min={minCredits}
                max={maxCredits}
                value={customAmount}
                onValueChange={setCustomAmount}
                variant='bordered'
                size='sm'
                className='flex-1'
                classNames={{ inputWrapper: 'h-10 min-h-0 rounded-xl' }}
              />
              <button
                disabled={purchasing !== null || customAmountNum < minCredits}
                onClick={handleCustomPurchase}
                className='h-10 px-5 rounded-xl bg-tokens-primary-2 text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap'
              >
                {purchasing?.startsWith('custom-')
                  ? '...'
                  : customAmountNum >= minCredits
                    ? `Buy · $${customPrice.toFixed(0)}`
                    : 'Buy'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
